import React from 'react';
import {
  EntityAboutCard,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  EntityHasSubcomponentsCard,
} from '@backstage/plugin-catalog';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { FullPage, Grid } from '@dewise-devex/backstage-plugin-mui-uikit-react';
import {
  EntityListProvider,
  InspectEntityDialog,
  UnregisterEntityDialog,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import {
  EntityControls,
  useEntityControl,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { GenericError } from '../../errors/GenericError';
import { Entity } from '@backstage/catalog-model';

/**
 * `entityWarningContent` renders a set of entity-specific warnings.
 *
 * - Warnings include:
 *   - Orphan entity warning
 *   - Relation warning
 *   - Catalog processing errors
 *
 * @returns {JSX.Element} A JSX fragment containing the warnings.
 *
 * @category Entity
 */
export const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid size={{ xs: 12 }}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid size={{ xs: 12 }}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid size={{ xs: 12 }}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

/**
 * `overviewContent` organizes entity-related cards into a grid layout.
 *
 * - Contains:
 *   - Warning components (`entityWarningContent`)
 *   - Entity metadata (`EntityAboutCard`)
 *   - Catalog graph visualization (`EntityCatalogGraphCard`)
 *   - Entity links (`EntityLinksCard`)
 *   - Subcomponents (`EntityHasSubcomponentsCard`)
 *
 * @returns {JSX.Element} The overview layout for the entity.
 *
 * @category Entity
 */
export const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid size={{ xs: 12, md: 6 }} sx={{ borderRadius: '12px' }}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    <Grid size={{ xs: 12, md: 6 }}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>
    <Grid size={{ xs: 12, md: 2 }}>
      <EntityLinksCard />
    </Grid>
    <Grid size={{ xs: 12, md: 8 }}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

export interface LayoutContentProps {
  entity: Entity;
  children: React.ReactNode;
}
const LayoutContent = (props: LayoutContentProps): React.ReactElement => {
  const {
    setUnregisterDialogOpen,
    setInspectDialogOpen,
    unregisterDialogOpen,
    handleInspectClose,
    handleUnregisterClose,
    handleUnregisterConfirm,
    inspectDialogOpen,
  } = useEntityControl(props.entity);

  return (
    <EntityListProvider>
      <FullPage
        variant="card"
        HeaderComponent={() => (
          <EntityControls
            entity={props.entity}
            setUnregisterDialogOpen={setUnregisterDialogOpen}
            setInspectDialogOpen={setInspectDialogOpen}
          />
        )}
      >
        {overviewContent}
        <UnregisterEntityDialog
          open={unregisterDialogOpen}
          onClose={handleUnregisterClose}
          entity={props.entity}
          onConfirm={handleUnregisterConfirm}
        />
        <InspectEntityDialog
          open={inspectDialogOpen}
          onClose={handleInspectClose}
          entity={props.entity}
        />
      </FullPage>
    </EntityListProvider>
  );
};

/**
 * `DefaultEntityLayout` renders the main layout for an entity.
 *
 * - Features:
 *   - Header with entity kind and type
 *   - Overview content with entity-related information
 *   - Support for unregistering and inspecting entities
 *
 * @returns {JSX.Element} The default entity layout component.
 *
 * @category Entity
 */
export const DefaultEntityLayout = (): React.ReactElement => {
  const { entity } = useAsyncEntity();

  if (entity) {
    return <LayoutContent entity={entity}>{overviewContent}</LayoutContent>;
  }

  return <GenericError />;
};
